import React, { useCallback, useEffect, useState } from 'react';
import { Clock, Loader2, Timer, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { inquiriesAPI } from '../../services/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const days = (value) => (value === null || value === undefined ? '—' : `${value} d`);
const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : '—');

export const InquiryCycleTimeCard = ({ inquiryId }) => {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState('');
  const [note, setNote] = useState('');
  const [workDate, setWorkDate] = useState(new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await inquiriesAPI.cycleTime(inquiryId);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not load time tracking');
    }
  }, [inquiryId]);

  useEffect(() => { load(); }, [load]);

  const addLog = async () => {
    const value = Number(hours);
    if (!value || value <= 0) { toast.error('Enter hours worked'); return; }
    setSaving(true);
    try {
      await inquiriesAPI.addTimeLog(inquiryId, { hours: value, work_date: workDate, note });
      setHours(''); setNote('');
      toast.success('Time logged');
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not log time');
    } finally { setSaving(false); }
  };

  const removeLog = async (logId) => {
    try {
      await inquiriesAPI.deleteTimeLog(inquiryId, logId);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not remove entry');
    }
  };

  if (!data) {
    return <div className="h-24 animate-pulse border bg-white" data-testid="inquiry-cycle-time-loading" />;
  }

  return (
    <section className="border bg-white p-5" data-testid="inquiry-cycle-time-card">
      <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex items-center gap-2">
          <Timer className="h-4 w-4 text-blue-600" />
          <h2 className="font-heading font-bold">Time spent on this inquiry</h2>
        </div>
        <p className="text-xs text-muted-foreground">{data.basis}</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <div className="border p-3" data-testid="cycle-total-days">
          <p className="text-xs text-muted-foreground">{data.is_closed ? 'Total cycle' : 'Age so far'}</p>
          <p className="font-mono text-xl font-bold">{days(data.total_working_days)}</p>
        </div>
        <div className="border p-3" data-testid="cycle-days-to-quote">
          <p className="text-xs text-muted-foreground">Received → quoted</p>
          <p className="font-mono text-xl font-bold">{days(data.days_to_quote)}</p>
        </div>
        <div className="border p-3" data-testid="cycle-current-stage">
          <p className="text-xs text-muted-foreground">
            {data.is_closed ? 'Final stage' : `Waiting in ${data.current_stage_label}`}
          </p>
          <p className="font-mono text-xl font-bold">
            {data.is_closed ? data.current_stage_label : days(data.days_in_current_stage)}
          </p>
        </div>
        <div className="border p-3" data-testid="cycle-logged-hours">
          <p className="text-xs text-muted-foreground">Logged effort</p>
          <p className="font-mono text-xl font-bold">{data.logged_hours} h</p>
        </div>
      </div>

      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[520px] text-sm">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-2">Stage</th><th className="py-2">Reached</th><th className="py-2 text-right">Working days</th>
            </tr>
          </thead>
          <tbody data-testid="cycle-stage-table">
            {data.stages.map((stage) => (
              <tr key={stage.key} className="border-b last:border-0" data-testid={`cycle-stage-row-${stage.key}`}>
                <td className="py-2">{stage.label}</td>
                <td className="py-2 text-muted-foreground">{stage.to ? formatDate(stage.to) : 'not reached'}</td>
                <td className="py-2 text-right font-mono">{days(stage.working_days)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-5 border-t pt-4">
        <p className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Clock className="h-3.5 w-3.5" /> Log effort (optional)
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={workDate} onChange={(event) => setWorkDate(event.target.value)}
            className="w-[150px]" data-testid="time-log-date-input" />
          <Input type="number" step="0.25" min="0" placeholder="Hours" value={hours}
            onChange={(event) => setHours(event.target.value)} className="w-[110px]" data-testid="time-log-hours-input" />
          <Input placeholder="What did you work on?" value={note}
            onChange={(event) => setNote(event.target.value)} className="min-w-[200px] flex-1" data-testid="time-log-note-input" />
          <Button onClick={addLog} disabled={saving} data-testid="time-log-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Log time'}
          </Button>
        </div>
        {data.time_logs.length > 0 && (
          <div className="mt-3 space-y-2" data-testid="time-log-list">
            {data.time_logs.map((log) => (
              <div key={log.id} className="flex items-center gap-3 border-l-2 border-blue-200 pl-3 text-sm"
                data-testid={`time-log-${log.id}`}>
                <span className="font-mono font-semibold">{log.hours} h</span>
                <span className="text-muted-foreground">{formatDate(log.work_date)}</span>
                <span className="flex-1 truncate">{log.note || '—'}</span>
                <span className="text-xs text-muted-foreground">{log.user_name}</span>
                <Button size="icon" variant="ghost" onClick={() => removeLog(log.id)}
                  data-testid={`time-log-delete-${log.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default InquiryCycleTimeCard;
